import type * as Types from '@aeppic/types'
import type { ParsedField, ParsedPlaceholder } from '@aeppic/forms-parser'
import { parseSelector } from '../lookup/form-oriented-lookup.js'
import { lookupDefaultControlName } from '../utils/control-names.js'
import { useServerLookup } from '../lookup/client-lookup.js'

export interface ControlLookupVariant {
  namespace?: string
  prefix?: string
  suffix?: string
}

export function normalizeControlLookupVariant(variant: ControlLookupVariant = {}): ControlLookupVariant {
  const normalized: ControlLookupVariant = {}
  for (const key of ['namespace', 'prefix', 'suffix'] as const) {
    const value = variant[key]
    if (value !== undefined && typeof value !== 'string') throw new TypeError(`Control variant ${key} must be a string`)
    if (value !== undefined && (key === 'namespace' || value !== '')) normalized[key] = value
  }
  return normalized
}

export function transformControlSelector(selector: string, field: ParsedField | null, variant: ControlLookupVariant = {}): string {
  const resolved = resolveControlSelector(selector, field, variant)
  return resolved ? `${resolved.namespace ? resolved.namespace + ':' : ''}${resolved.name}` : ''
}

const instanceIds = new WeakMap<object, number>()
let nextInstanceId = 0
export function controlPreloadIdentity(aeppic, form: { id: string, v: string }, variant: ControlLookupVariant = {}): string {
  // Context wrappers share Forms with their root instance, but independent instances do not.
  const owner = aeppic.Forms ?? aeppic
  if (!instanceIds.has(owner)) instanceIds.set(owner, ++nextInstanceId)
  const options = aeppic.Features?.getOptionsFor?.('control-lookup') ?? {}
  return JSON.stringify([instanceIds.get(owner), aeppic.Account?.id ?? null, form.id, form.v,
    aeppic.Features?.isEnabled?.('control-lookup') !== false, options.strategy ?? 'full',
    options.fallback === true, aeppic.LocalModel?.enabled === true, useServerLookup(aeppic, 'controls'), normalizeControlLookupVariant(variant)])
}

export interface ControlLookupFlight {
  offset: number
  priority: number
  start: () => Promise<Types.Document[]> | null
}

export interface SettledControlLookup {
  offset: number
  priority: number
  document: Types.Document | null
}

/**
 * Same selector ControlLookup uses for the name ae-control receives.
 * A field without an explicit control name uses the default control name.
 */
export function resolveControlSelector(controlSelector: string | null | undefined, field: ParsedField | null, variant: ControlLookupVariant = {}): { namespace: string, name: string } | null {
  const parsed = parseSelector(controlSelector || '')
  let { namespace, name } = parsed
  if (!field && !name) return null
  if (name.trim() === '') {
    if (!field?.subType) return null
    name = field.subType
  }
  const normalized = normalizeControlLookupVariant(variant)
  return { namespace: normalized.namespace ?? namespace, name: `${normalized.prefix ?? ''}${name}${normalized.suffix ?? ''}` }
}

export function controlNameForPlaceholder(placeholder: ParsedPlaceholder): string {
  if (placeholder.control) return placeholder.control.fullName || ''
  if (placeholder.field) return lookupDefaultControlName(placeholder.field)
  return ''
}

export function controlSelectorForPlaceholder(placeholder: ParsedPlaceholder, variant: ControlLookupVariant = {}): { namespace: string, name: string } | null {
  return resolveControlSelector(controlNameForPlaceholder(placeholder), placeholder.field ?? null, variant)
}

/**
 * Build lazy query plans. Creating a plan never starts a database read.
 * Consumers await each start() before requesting the next query.
 */
export function scheduleControlLookups(
  placeholders: ParsedPlaceholder[],
  locations: string[],
  startQuery: (locationId: string, field: ParsedField | null, namespace: string, name: string) => Promise<Types.Document[]> | null,
): ControlLookupFlight[] {
  const flights: ControlLookupFlight[] = []
  for (const placeholder of placeholders) {
    if (placeholder.offset == null) continue
    if (!placeholder.field && !placeholder.control) continue
    const selector = controlSelectorForPlaceholder(placeholder)
    if (!selector) continue
    for (let priority = 0; priority < locations.length; priority++) {
      flights.push({ offset: placeholder.offset, priority, start: () => startQuery(locations[priority], placeholder.field ?? null, selector.namespace, selector.name) })
    }
  }
  return flights
}

/** Earliest location wins. A placeholder with no match is stored as null. */
export function chooseControls(placeholders: ParsedPlaceholder[], results: SettledControlLookup[]): Map<number, Types.Document | null> {
  const chosen = new Map<number, Types.Document | null>()
  const winningPriority = new Map<number, number>()
  for (const placeholder of placeholders) {
    if (placeholder.offset == null) continue
    if (!placeholder.field && !placeholder.control) continue
    chosen.set(placeholder.offset, null)
  }
  for (const result of results) {
    if (!result.document) continue
    const current = winningPriority.get(result.offset)
    if (current == null || result.priority < current) {
      winningPriority.set(result.offset, result.priority)
      chosen.set(result.offset, result.document)
    }
  }
  return chosen
}
